import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  WxrlRuleDetail,
  WrxlRulesService,
  WxrlAuthor,
  WxrlCondition,
  WxrlAction,
  WxrlRuleTrigger,
} from '../../services/wxrl-rules.service';
import { AuthService } from '@app/core/auth';
import { rbac } from '@app/shared/rbac';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { WXRL_DIRECT_CONDITION, isDirectCondition } from '../../utils/wxrl-direct';
import { wxrlFormulaOrDirectRuleValidator } from '../../utils/wxrl-formula-or-direct.validator';
import { normalizeSnFlag, normalizeVisibleFlag } from '../../utils/wxrl-sn-flag';

@Component({
  selector: 'afoone-wxrl-rule-detail',
  templateUrl: './wxrl-rule-detail.component.html',
  styleUrls: ['./wxrl-rule-detail.component.css'],
  providers: [ConfirmationService],
})
export class WxrlRuleDetailComponent implements OnInit, OnDestroy {
  public rule: WxrlRuleDetail = null;
  public ruleNrs: number[] = [];
  public ruleForm: FormGroup;
  private originalModWHen: string | null = null; // Guardar el valor original antes de formatear
  /** Valor API de DateCount antes de formatear a d/m/y (#542) */
  private originalDateCount: string | Date | null = null;
  public editMode: boolean = false;
  public isNewRule: boolean = false;
  public labNr: number | null = null;
  /** Índice activo del TabView; al entrar en modo edición se fuerza al tab Esquema. */
  public activeTabIndex: number = 0;
  /** Lista de estados de acción (value=Schl, label=Schluessel) para resolver label→código y mostrar en tabla */
  public actionStates: Array<{ value: string; label: string }> = [];
  /** Mismo permiso que crear: usuario con permiso de edición en el laboratorio de la regla (PERSGROUP). */
  public hasEditPermissionForLab: boolean = false;
  /** Evita doble envío y estados incoherentes tras Guardar (#detalle regla). */
  public savingRule = false;
  private autoSaveSubscription: any = null;
  private readonly AUTO_SAVE_STORAGE_KEY_PREFIX = 'wxrl-rule-new-';
  /** Evita disparar lógica del checkbox al hidratar el form desde API (#705). */
  private patchingRule = false;
  private ruleHeaderDirectWatchersInit = false;

  constructor(
    private _route: ActivatedRoute,
    private __wxrlRulesService: WrxlRulesService,
    private _router: Router,
    private _authService: AuthService,
    private _fb: FormBuilder,
    private _confirmationService: ConfirmationService,
    private _toastrService: ToastrService,
    private _translate: TranslateService
  ) {
    this.ruleForm = this._fb.group(
      {
        ruleNr: [{ value: 0 }],
        ruleName: [''],
        ruleKrz: [''],
        kommentar: [''],
        condition: [''],
        negRule: [false],
        /** UI: RULES.Condition en BD será DIRECT (#705). */
        directExecution: [false],
        ruleActionNr: [null],
        conditions: this._fb.array([]),
        actions: this._fb.array([]),
        triggers: this._fb.array([]),
        actionHeader: this._fb.group({
          ruleActionNr: [null],
          ruleActionDesc: [''],
          labNr: [null],
          createdWhen: [null],
          info: [''],
          author: [null],
        }),
      },
      { validators: [wxrlFormulaOrDirectRuleValidator] }
    );
  }

  /**
   * Título de la tarjeta: en regla nueva, añade el ID entre paréntesis cuando ya está asignado.
   */
  get ruleDetailCardHeader(): string {
    if (!this.isNewRule) {
      const nr = this.rule?.ruleNr ?? '';
      return `${this._translate.instant('WXRL.RULE.DETAIL_HEADER')} ${nr}`;
    }
    const base = this._translate.instant('WXRL.CREATE.TITLE');
    const raw = this.rule?.ruleNr ?? this.ruleForm?.get('ruleNr')?.value;
    const num =
      raw === null || raw === undefined || raw === ''
        ? Number.NaN
        : Number(raw);
    if (Number.isFinite(num) && num > 0) {
      return `${base} (${num})`;
    }
    return base;
  }

  ngOnInit(): void {
    this._route.params.subscribe((params) => {
      const { id } = params;

      // Detectar si es modo nuevo
      if (id === 'new') {
        this.isNewRule = true;
        this.editMode = true;
        this.activeTabIndex = 0; // Esquema es la primera pestaña en reglas nuevas
        const labStr = this._route.snapshot.queryParamMap.get('labNr');
        const parsed =
          labStr != null && String(labStr).trim() !== ''
            ? Number(String(labStr).trim())
            : NaN;
        if (!Number.isInteger(parsed) || parsed <= 0) {
          this.labNr = null;
          this.__wxrlRulesService.handleError(new Error('WXRL.ERRORS.LAB_NR_REQUIRED'));
          void this._router.navigate(['/wxrl/rules']);
          return;
        }
        this.labNr = parsed;
        const suggestedRaw = this._route.snapshot.queryParamMap.get('suggestedRuleNr');
        if (suggestedRaw != null && String(suggestedRaw).trim() !== '') {
          const suggestedRuleNr = Number(String(suggestedRaw).trim());
          if (Number.isInteger(suggestedRuleNr) && suggestedRuleNr > 0) {
            this.ruleForm.patchValue({ ruleNr: suggestedRuleNr });
          }
        }
        this.initializeNewRule();
      } else {
        this.isNewRule = false;
        const ruleIdNum = +id;
        this.loadRule(id);

        // Lista de IDs para el desplegable: desde localStorage o solo esta regla
        const stored = localStorage.getItem('wxrlRulesruleNrs');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            this.ruleNrs = Array.isArray(parsed) ? parsed : [ruleIdNum];
          } catch (e) {
            console.error('Error parsing ruleNrs from localStorage', e);
            localStorage.removeItem('wxrlRulesruleNrs');
            this.ruleNrs = [ruleIdNum];
          }
        } else {
          this.ruleNrs = [ruleIdNum];
        }
        // Si la regla actual no está en la lista, sustituir por una lista con solo esta regla
        if (!this.ruleNrs.includes(ruleIdNum)) {
          this.ruleNrs = [ruleIdNum];
          localStorage.setItem('wxrlRulesruleNrs', JSON.stringify(this.ruleNrs));
        }
      }
    });

    // Verificar si viene el parámetro 'edit' en la query string
    this._route.queryParams.subscribe((queryParams) => {
      if (queryParams['edit'] === 'true' || queryParams['edit'] === true) {
        if (this.canEdit) {
          this.editMode = true;
          this.activeTabIndex = this.schemaTabIndex;
        }
      }
    });

    this.initRuleHeaderDirectWatchers();
  }

  /** Checkbox «Regla sin condiciones» + fórmula obligatoria si no DIRECT (#705). */
  private initRuleHeaderDirectWatchers(): void {
    if (this.ruleHeaderDirectWatchersInit) {
      return;
    }
    this.ruleHeaderDirectWatchersInit = true;
    this.ruleForm.get('condition')?.valueChanges.subscribe(() => {
      queueMicrotask(() => {
        this.ruleForm.updateValueAndValidity({ emitEvent: false });
      });
    });
    this.ruleForm.get('directExecution')?.valueChanges.subscribe((direct) => {
      if (this.patchingRule) {
        return;
      }
      if (direct) {
        const n = this.conditionsFormArray?.length ?? 0;
        if (n > 0) {
          this._confirmationService.confirm({
            message: this._translate.instant(
              'WXRL.RULE.FORM.NO_CONDITIONS_RULE_CONFIRM'
            ),
            header: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
              this.conditionsFormArray.clear();
              if (this.rule) {
                this.rule.conditions = [];
              }
              this.ruleForm.patchValue({ condition: '' }, { emitEvent: false });
              this.ruleForm.markAsDirty();
              queueMicrotask(() => {
                this.ruleForm.updateValueAndValidity({ emitEvent: false });
              });
            },
            reject: () => {
              this.ruleForm
                .get('directExecution')
                ?.patchValue(false, { emitEvent: false });
            },
          });
        } else {
          this.ruleForm.patchValue({ condition: '' }, { emitEvent: false });
          this.ruleForm.markAsDirty();
          queueMicrotask(() => {
            this.ruleForm.updateValueAndValidity({ emitEvent: false });
          });
        }
      } else {
        queueMicrotask(() => {
          this.ruleForm.updateValueAndValidity({ emitEvent: false });
        });
      }
    });
  }

  private loadRule(id: string | number): Promise<void> {
    return this.__wxrlRulesService.getRule(id.toString()).then(async (rule) => {
      this.rule = rule;
      this.labNr = rule.actionHeader?.labNr ?? rule.labNr ?? this.labNr;
      const labResolved =
        this.labNr != null ? Number(this.labNr) : NaN;
      if (!Number.isInteger(labResolved) || labResolved <= 0) {
        this.__wxrlRulesService.handleError(new Error('WXRL.ERRORS.LAB_NR_REQUIRED'));
        void this._router.navigate(['/wxrl/rules']);
        return;
      }
      this.labNr = labResolved;

      // Cargar estados de acción para resolver label→código (Schluessel→Schl) y mostrar en tabla
      try {
        const states = await this.__wxrlRulesService.getActionStates();
        this.actionStates = states.map((s) => ({ value: s.schl, label: s.schluessel }));
      } catch {
        this.actionStates = [];
      }

      this.patchingRule = true;
      try {
        this.ruleForm.patchValue(
          {
            ruleNr: rule.ruleNr,
            ruleName: rule.ruleName,
            ruleKrz: rule.ruleKrz,
            kommentar: rule.kommentar,
            condition: isDirectCondition(rule.condition) ? '' : rule.condition,
            negRule: rule.negRule,
            directExecution: isDirectCondition(rule.condition),
            ruleActionNr: rule.ruleActionNr || null,
          },
          { emitEvent: false }
        );
      } finally {
        this.patchingRule = false;
        this.ruleForm.updateValueAndValidity({ emitEvent: false });
      }

      // Load actionHeader if exists
      if (rule.actionHeader) {
        this.ruleForm.get('actionHeader')?.patchValue({
          ruleActionNr: rule.actionHeader.ruleActionNr,
          ruleActionDesc: rule.actionHeader.ruleActionDesc,
          labNr: rule.actionHeader.labNr,
          createdWhen: rule.actionHeader.createdWhen,
          info: rule.actionHeader.info,
          author: rule.actionHeader.author
        });
      }

      // Clear and populate conditions FormArray
      this.conditionsFormArray.clear();
      if (rule.conditions) {
        rule.conditions.forEach((condition) => {
          this.conditionsFormArray.push(this.createConditionFormGroup(condition));
        });
      }

      // Clear and populate actions FormArray (ruleState en API viene como etiqueta; guardamos código)
      this.actionsFormArray.clear();
      if (rule.actions) {
        rule.actions.forEach((action) => {
          const resolvedRuleState = this.resolveActionStateToCode(action.ruleState);
          this.actionsFormArray.push(this.createActionFormGroup({ ...action, ruleState: resolvedRuleState }));
        });
      }

      // Clear and populate triggers FormArray
      this.triggersFormArray.clear();
      if (rule.triggers) {
        rule.triggers.forEach((trigger) => {
          this.triggersFormArray.push(this.createTriggerFormGroup(trigger));
        });
      }

      // Guardar el valor original de modWHen antes de formatearlo
      this.originalModWHen = rule.modWHen;
      this.originalDateCount = rule.dateCount ?? null;

      // Format dates
      const modWHen = new Date(rule.modWHen);
      this.rule.modWHen = `${modWHen.getDate()}/${
        modWHen.getMonth() + 1
      }/${modWHen.getFullYear()}`;

      const dateCount = new Date(rule.dateCount);
      if (!Number.isNaN(dateCount.getTime())) {
        this.rule.dateCount = `${dateCount.getDate()}/${
          dateCount.getMonth() + 1
        }/${dateCount.getFullYear()}`;
      }

      this.rule.resume = this.rule.resume?.replace(/\r/g, '\n') || '';

      // Mismo permiso que crear: comprobar permiso de edición en el laboratorio (PERSGROUP).
      // Debe completarse dentro de esta promesa para que loadRule().then() no cierre edición antes de tiempo.
      if (this.labNr != null) {
        try {
          const r = await this.__wxrlRulesService.checkCreatePermission(this.labNr);
          this.hasEditPermissionForLab = r.hasPermission;
          if (!r.hasPermission && this.editMode) {
            this.editMode = false;
          }
        } catch {
          this.hasEditPermissionForLab = false;
          if (this.editMode) this.editMode = false;
        }
      } else {
        this.hasEditPermissionForLab = false;
        if (this.editMode) this.editMode = false;
      }

      this.syncRuleHeaderControlsEditState();
    });
  }

  /**
   * negRule / directExecution: usar disable() en el FormControl, no [disabled] en template (#705).
   */
  private syncRuleHeaderControlsEditState(): void {
    const editable = this.isNewRule || (this.editMode && this.canEditThisRule);
    const opts = { emitEvent: false };
    const negRule = this.ruleForm.get('negRule');
    const directExecution = this.ruleForm.get('directExecution');
    if (editable) {
      negRule?.enable(opts);
      directExecution?.enable(opts);
    } else {
      negRule?.disable(opts);
      directExecution?.disable(opts);
    }
  }

  private createConditionFormGroup(condition: WxrlCondition): FormGroup {
    return this._fb.group({
      condRule: [condition.condRule],
      posC: [condition.posC],
      kommentar: [condition.kommentar],
      condId: [condition.condId],
      saveValue: [condition.saveValue],
      methName: [condition.methName],
      grupNr: [condition.grupNr],
      rOperator: [condition.rOperator],
      idOperator: [condition.idOperator ?? ''],
      catalogROperator: [condition.catalogROperator ?? ''],
      labelOperator: [condition.labelOperator],
      entryValue: [condition.entryValue],
      negCond: [normalizeSnFlag(condition.negCond, 'N')],
      exitCond: [normalizeSnFlag(condition.exitCond, 'N')],
      visible: [normalizeVisibleFlag(condition.visible, 'S')],
      isolate: [condition.isolate],
      tipoRes: [condition.tipoRes],
      debugCond: [(condition as WxrlCondition & { debugCond?: number | null }).debugCond ?? null],
      relation: [condition.relation ?? null],
    });
  }

  private createActionFormGroup(action: WxrlAction): FormGroup {
    return this._fb.group({
      ruleState: [action.ruleState],
      posA: [action.posA],
      ruleActionDesc: [action.ruleActionDesc],
      actionKrz: [action.actionKrz],
      labelAction: [action.labelAction ?? ''],
      methNr: [action.methNr],
      grupNr: [action.grupNr],
      textNr: [action.textNr],
      actionValue: [action.actionValue],
      additional1: [action.additional1],
      additional2: [action.additional2],
      persNr: [action.persNr],
      kommentar: [action.kommentar],
    });
  }

  /**
   * Resuelve el estado de acción: si viene la etiqueta (Schluessel) del API, devuelve el código (Schl)
   * para que el backend persista el valor correcto en RULESACTION.ActionState.
   */
  private resolveActionStateToCode(ruleState: string | undefined): string {
    if (ruleState == null || ruleState === '') return ruleState ?? '';
    if (!this.actionStates?.length) return ruleState;
    const match = this.actionStates.find(
      (s) =>
        s.value === ruleState ||
        (s.label && String(s.label).toLowerCase() === String(ruleState).toLowerCase())
    );
    return match?.value ?? ruleState;
  }

  private createTriggerFormGroup(trigger: WxrlRuleTrigger): FormGroup {
    return this._fb.group({
      subjectType: [trigger.subjectType],
      subjectTypeDescription: [(trigger as any).subjectTypeDescription || null],
      subjectID: [trigger.subjectID],
      subjectName: [trigger.subjectName],
      methNrFilter: [trigger.methNrFilter],
      alarmFilter: [trigger.alarmFilter],
      posR: [trigger.posR],
      ruleState: [trigger.ruleState],
      ruleStateDescription: [(trigger as any).ruleStateDescription || null],
      triggEvent: [trigger.triggEvent],
      triggEventDescription: [(trigger as any).triggEventDescription || null],
      auftrNrTest: [trigger.auftrNrTest],
      debug: [trigger.debug],
      kommentar: [trigger.kommentar],
    });
  }

  /** ExecCount / ExecCountAction: entero >= 0 para payload de guardado (#542). */
  private normalizeRuleCounter(value: unknown): number {
    if (value === undefined || value === null || value === '') return 0;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }

  // Getters for FormArrays
  get conditionsFormArray(): FormArray {
    return this.ruleForm.get('conditions') as FormArray;
  }

  /** Condiciones para el editor de fórmula en cabecera (#540: mismo patrón que drawer / esquema). */
  getConditionsForFormulaEditor(): Array<{ condRule: string; kommentar: string }> {
    if (!this.conditionsFormArray) {
      return [];
    }
    return this.conditionsFormArray.controls.map((control) => ({
      condRule: control.value.condRule || '',
      kommentar:
        control.value.kommentar || control.value.methName || '',
    }));
  }

  get actionsFormArray(): FormArray {
    return this.ruleForm.get('actions') as FormArray;
  }

  get triggersFormArray(): FormArray {
    return this.ruleForm.get('triggers') as FormArray;
  }

  onRuleNrChange(ruleNr: number): void {
    this._router.navigate(['wxrl/rules', ruleNr]);
  }

  get canEdit(): boolean {
    return (
      this._authService.hasProfile(rbac.profiles.WRULES_USER) ||
      this._authService.hasProfile(rbac.profiles.WRULES_ADMIN)
    );
  }

  /** Puede editar esta regla: perfil WRULES_USER/ADMIN y permiso en el laboratorio (mismo criterio que crear). */
  get canEditThisRule(): boolean {
    if (!this.canEdit) return false;
    if (this.isNewRule) return true;
    return this.hasEditPermissionForLab;
  }

  get canEditSchema(): boolean {
    return (this.canEditThisRule && this.editMode) || this.isNewRule;
  }

  /** Reset contador: permiso laboratorio + perfil (#543); no exige modo edición. */
  get canResetCounters(): boolean {
    return (
      !this.isNewRule &&
      this.canEditThisRule &&
      this.labNr != null &&
      !!this.rule?.ruleNr
    );
  }

  onResetCounters(): void {
    if (!this.canResetCounters || this.rule == null) {
      return;
    }
    this._confirmationService.confirm({
      message: this._translate.instant('WXRL.RULE.RESET_COUNTER_CONFIRM'),
      header: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        void this.performResetCounters();
      },
    });
  }

  private async performResetCounters(): Promise<void> {
    if (!this.canResetCounters || this.rule == null || this.labNr == null) {
      return;
    }
    try {
      await this.__wxrlRulesService.resetRuleCounters(this.rule.ruleNr, this.labNr);
      await this.loadRule(String(this.rule.ruleNr));
      this._toastrService.success(
        this._translate.instant('WXRL.RULE.RESET_COUNTER_SUCCESS')
      );
    } catch (err) {
      this.__wxrlRulesService.handleError(err);
    }
  }

  /** Índice del tab Esquema: 0 en reglas nuevas, 1 en reglas existentes (tras Resumen). */
  get schemaTabIndex(): number {
    return this.isNewRule ? 0 : 1;
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (this.editMode) {
      this.activeTabIndex = this.schemaTabIndex;
    }
    this.syncRuleHeaderControlsEditState();
    console.log('Edit mode toggled:', this.editMode);
  }

  get cumplimiento(): string {
    if (!this.rule) {
      return '0%';
    }
    const execCount = this.normalizeRuleCounter(this.rule.execCount);
    const execCountAction = this.normalizeRuleCounter(this.rule.execCountAction);
    if (execCount === 0) {
      return '0%';
    }
    const pct = Math.round((execCountAction / execCount) * 100);
    return `${Number.isFinite(pct) ? pct : 0}%`;
  }

  get author(): WxrlAuthor | null {
    if (this.rule?.author && typeof this.rule.author === 'object' && this.rule.author !== null) {
      return this.rule.author;
    } else {
      return null;
    }
  }

  get modified(): WxrlAuthor | null {
    if (this.rule?.modified && typeof this.rule.modified === 'object' && this.rule.modified !== null) {
      return this.rule.modified;
    } else {
      return null;
    }
  }

  /**
   * Obtiene la fecha de modificación correcta.
   * Si modified es 0, usa createdWhen.
   * Si no, usa modWHen, pero solo si no es la fecha por defecto (1900-01-01).
   */
  get modifiedWhen(): string | null {
    if (!this.rule || this.isNewRule) {
      return null;
    }

    // Si modified es 0 (número), no ha sido modificado, usar createdWhen
    if (typeof this.rule.modified === 'number' && this.rule.modified === 0) {
      return this.rule.createdWhen;
    }

    // Si modified es un objeto (WxrlAuthor), usar modWHen original
    if (typeof this.rule.modified === 'object' && this.rule.modified !== null) {
      // Usar el valor original antes de formatear
      const modWHenToCheck = this.originalModWHen || this.rule.modWHen;

      // Verificar si modWHen no es la fecha por defecto
      const modDate = new Date(modWHenToCheck);
      const defaultDate = new Date('1900-01-01T00:00:00.000Z');

      // Si la fecha es diferente a la fecha por defecto, usar modWHen original
      if (modDate.getTime() !== defaultDate.getTime()) {
        return modWHenToCheck;
      }
    }

    // Si no hay modificación válida, usar createdWhen
    return this.rule.createdWhen;
  }

  get labType(): string | undefined {
    return this.rule?.labType;
  }

  onFirstRule(): void {
    const firstRule = this.ruleNrs[0];
    this._router.navigate(['wxrl/rules', firstRule]);
  }

  onLastRule() {
    const lastRule = this.ruleNrs[this.ruleNrs.at(-1)];
    this._router.navigate(['wxrl/rules', lastRule]);
  }

  onPreviousRule(): void {
    if (!this.rule || this.isNewRule) {
      return;
    }
    const currentRuleIndex = this.ruleNrs.indexOf(this.rule.ruleNr);
    if (currentRuleIndex > 0) {
      const previousRule = this.ruleNrs[currentRuleIndex - 1];
      this._router.navigate(['wxrl/rules', previousRule]);
    }
  }

  onNextRule(): void {
    if (!this.rule || this.isNewRule) {
      return;
    }
    const currentRuleIndex = this.ruleNrs.indexOf(this.rule.ruleNr);
    if (currentRuleIndex < this.ruleNrs.length - 1) {
      const nextRule = this.ruleNrs[currentRuleIndex + 1];
      this._router.navigate(['wxrl/rules', nextRule]);
    }
  }

  get hasPreviousRule(): boolean {
    if (!this.rule || this.isNewRule) {
      return false;
    }
    const currentRuleIndex = this.ruleNrs.indexOf(this.rule.ruleNr);
    return currentRuleIndex > 0;
  }

  get hasNextRule(): boolean {
    if (!this.rule || this.isNewRule) {
      return false;
    }
    const currentRuleIndex = this.ruleNrs.indexOf(this.rule.ruleNr);
    return currentRuleIndex < this.ruleNrs.length - 1;
  }

  async initializeNewRule(): Promise<void> {
    try {
      // Obtener maxRuleNr + 1 si no viene en queryParams
      if (!this.ruleForm.value.ruleNr) {
        const maxRuleNrResult = await this.__wxrlRulesService.getMaxRuleNr();
        const suggestedRuleNr = (maxRuleNrResult.maxRuleNr || 0) + 1;
        this.ruleForm.patchValue({ ruleNr: suggestedRuleNr });
      }

      // Inicializar rule con valores por defecto vacíos para que el template se muestre
      this.originalDateCount = null;
      this.rule = {
        ruleNr: this.ruleForm.value.ruleNr || 0,
        ruleName: '',
        ruleKrz: '',
        kommentar: '',
        condition: '',
        negRule: false,
        ruleActionNr: null,
        execCount: 0,
        execCountAction: 0,
        dateCount: new Date().toISOString(),
        resume: '',
        conditions: [],
        actions: [],
        triggers: [],
        actionHeader: null,
        createdWhen: new Date().toISOString(),
        modWHen: new Date().toISOString(),
        author: null,
        modified: null,
      };

      // Restaurar desde localStorage si existe
      this.restoreFromLocalStorage();

      // Inicializar autosave
      this.setupAutoSave();
      this.syncRuleHeaderControlsEditState();
    } catch (error) {
      console.error('Error initializing new rule:', error);
      this.__wxrlRulesService.handleError(error);
    }
  }

  private setupAutoSave(): void {
    // Limpiar suscripción anterior si existe
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }

    // Suscribirse a cambios del formulario con debounce
    this.autoSaveSubscription = this.ruleForm.valueChanges
      .pipe(debounceTime(5000)) // Guardar después de 5 segundos de inactividad
      .subscribe(() => {
        this.autoSaveToLocalStorage();
      });
  }

  private autoSaveToLocalStorage(): void {
    if (!this.isNewRule || !this.labNr || !this.ruleForm.value.ruleNr) {
      return;
    }

    const storageKey = `${this.AUTO_SAVE_STORAGE_KEY_PREFIX}${this.labNr}-${this.ruleForm.value.ruleNr}`;
    try {
      const formData = this.ruleForm.value;
      localStorage.setItem(storageKey, JSON.stringify({
        formData,
        labNr: this.labNr,
        savedAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  private restoreFromLocalStorage(): void {
    if (!this.isNewRule || !this.labNr || !this.ruleForm.value.ruleNr) {
      return;
    }

    const storageKey = `${this.AUTO_SAVE_STORAGE_KEY_PREFIX}${this.labNr}-${this.ruleForm.value.ruleNr}`;
    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.formData) {
          this.ruleForm.patchValue(parsed.formData, { emitEvent: false });
        }
      }
    } catch (error) {
      console.error('Error restoring from localStorage:', error);
    }
  }

  private clearAutoSave(): void {
    if (!this.isNewRule || !this.labNr || !this.ruleForm.value.ruleNr) {
      return;
    }

    const storageKey = `${this.AUTO_SAVE_STORAGE_KEY_PREFIX}${this.labNr}-${this.ruleForm.value.ruleNr}`;
    localStorage.removeItem(storageKey);
  }

  private hasUnsavedChanges(): boolean {
    if (!this.isNewRule) {
      return false;
    }

    const storageKey = `${this.AUTO_SAVE_STORAGE_KEY_PREFIX}${this.labNr}-${this.ruleForm.value.ruleNr}`;
    const savedData = localStorage.getItem(storageKey);
    if (!savedData) {
      // Si no hay datos guardados, verificar si el formulario tiene valores
      const formValue = this.ruleForm.value;
      return !!(
        formValue.ruleName ||
        formValue.ruleKrz ||
        formValue.kommentar ||
        formValue.condition ||
        formValue.directExecution ||
        this.conditionsFormArray.length > 0 ||
        this.actionsFormArray.length > 0 ||
        this.triggersFormArray.length > 0
      );
    }

    const parsed = JSON.parse(savedData);
    const currentFormData = JSON.stringify(this.ruleForm.value);
    return currentFormData !== JSON.stringify(parsed.formData);
  }

  ngOnDestroy(): void {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
  }

  onSave(): void {
    if (!this.ruleForm.valid || this.savingRule) {
      return;
    }

    // Contadores solo en servidor: tomar snapshot antes del merge para no enviar 0 en un segundo guardado
    // si la respuesta PUT fuera parcial o this.rule quedara inconsistente.
    const counterSnapshot =
      !this.isNewRule && this.rule
        ? {
            execCount: this.rule.execCount,
            execCountAction: this.rule.execCountAction,
          }
        : null;

    // getRawValue() incluye controles deshabilitados (p. ej. methNr cuando "heredar" está marcado)
    const formValue = this.ruleForm.getRawValue();
    const { directExecution, ...formWithoutDirectFlag } = formValue;

    const actionsFromArray =
      this.actionsFormArray?.length > 0
        ? this.actionsFormArray.controls.map((c) => {
            const g = c as FormGroup;
            return g.getRawValue ? g.getRawValue() : c.value;
          })
        : [];
    let actionsToSend: any[] = Array.isArray(formValue.actions) ? formValue.actions : [];
    if (actionsFromArray.length > 0) {
      actionsToSend = actionsFromArray;
    } else if (this.rule?.actions && this.rule.actions.length > 0) {
      actionsToSend = this.rule.actions;
    }

    const triggersFromArray =
      this.triggersFormArray?.length > 0
        ? this.triggersFormArray.controls.map((c) => {
            const g = c as FormGroup;
            return g.getRawValue ? g.getRawValue() : c.value;
          })
        : [];
    let triggersToSend: any[] = this.rule?.triggers ?? [];
    if (Array.isArray(formValue.triggers) && formValue.triggers.length > 0) {
      triggersToSend = formValue.triggers;
    } else if (triggersFromArray.length > 0) {
      triggersToSend = triggersFromArray;
    }

    const ruleToSave: any = {
      ...(this.rule || {}),
      ...formWithoutDirectFlag,
      condition: directExecution
        ? WXRL_DIRECT_CONDITION
        : String(formValue.condition ?? '').trim(),
      actions: actionsToSend,
      triggers: triggersToSend,
      actionHeader: {
        ...(this.rule?.actionHeader || {}),
        ...(formValue.actionHeader || {}),
        labNr: formValue.actionHeader?.labNr ?? this.rule?.actionHeader?.labNr ?? undefined,
      },
    };

    // El backend exige labNr en el body tanto al crear como al actualizar
    const labNrToSend = this.isNewRule
      ? this.labNr
      : (this.rule?.actionHeader?.labNr ?? this.rule?.labNr ?? formValue.actionHeader?.labNr ?? this.labNr);
    const labNrFinal = labNrToSend ?? ruleToSave.actionHeader?.labNr ?? this.rule?.labNr;
    if (labNrFinal == null) {
      const msgKey = this.isNewRule ? 'WXRL.CREATE.ERROR_NO_LAB' : 'WXRL.EDIT_MODE.ERROR_NO_LAB';
      this._toastrService.error(this._translate.instant(msgKey) || (this.isNewRule ? 'Debe indicar el laboratorio de la regla.' : 'La regla debe tener un laboratorio asociado. Indíquelo en la pestaña Esquema, en la cabecera de la acción.'));
      return;
    }
    ruleToSave.labNr = labNrFinal;
    if (ruleToSave.actionHeader) {
      ruleToSave.actionHeader.labNr = labNrFinal;
      const desc = ruleToSave.actionHeader.ruleActionDesc;
      if (desc == null || String(desc).trim() === '') {
        ruleToSave.actionHeader.ruleActionDesc = ' ';
      }
    }

    if (counterSnapshot) {
      ruleToSave.execCount = this.normalizeRuleCounter(counterSnapshot.execCount);
      ruleToSave.execCountAction = this.normalizeRuleCounter(
        counterSnapshot.execCountAction
      );
    } else {
      ruleToSave.execCount = this.normalizeRuleCounter(ruleToSave.execCount);
      ruleToSave.execCountAction = this.normalizeRuleCounter(
        ruleToSave.execCountAction
      );
    }
    if (
      this.originalDateCount != null &&
      String(this.originalDateCount).trim() !== ''
    ) {
      ruleToSave.dateCount = this.originalDateCount;
    } else if (
      this.isNewRule &&
      (ruleToSave.dateCount === undefined ||
        ruleToSave.dateCount === null ||
        ruleToSave.dateCount === '')
    ) {
      ruleToSave.dateCount = new Date().toISOString();
    }

    console.log('Saving rule', ruleToSave);

    this.savingRule = true;
    const savePromise = this.isNewRule
      ? this.__wxrlRulesService.createRule(ruleToSave)
      : this.__wxrlRulesService.updateRule(this.rule.ruleNr, ruleToSave);

    savePromise
      .then((savedRule) => {
        // Limpiar autosave si es modo nuevo
        if (this.isNewRule) {
          this.clearAutoSave();
          // Navegar a la nueva regla
          this._router.navigate(['/wxrl/rules', savedRule.ruleNr]);
          return Promise.resolve();
        }

        // Salir de edición en cuanto el guardado OK; quitar ?edit=true para que no se reactive el modo.
        this.editMode = false;
        this._router.navigate([], {
          relativeTo: this._route,
          queryParams: { edit: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });

        this.rule = {
          ...savedRule,
          execCount:
            savedRule.execCount ??
            this.normalizeRuleCounter(counterSnapshot?.execCount),
          execCountAction:
            savedRule.execCountAction ??
            this.normalizeRuleCounter(counterSnapshot?.execCountAction),
        };
        this.ruleForm.patchValue(
          {
            ruleNr: savedRule.ruleNr,
            ruleName: savedRule.ruleName,
            ruleKrz: savedRule.ruleKrz,
            kommentar: savedRule.kommentar,
            condition: isDirectCondition(savedRule.condition)
              ? ''
              : savedRule.condition,
            negRule: savedRule.negRule,
            directExecution: isDirectCondition(savedRule.condition),
            ruleActionNr: savedRule.ruleActionNr || null,
          },
          { emitEvent: false }
        );
        console.log('Rule saved successfully', savedRule);
        return this.loadRule(this.rule.ruleNr);
      })
      .then(() => {
        if (!this.isNewRule) {
          this.ruleForm.markAsPristine();
        }
      })
      .catch((error) => {
        console.error('Error saving rule', error);
        this.__wxrlRulesService.handleError(error);
      })
      .finally(() => {
        this.savingRule = false;
      });
  }

  /**
   * Cancel en el drawer de cabecera durante el solo primer acceso automático (regla nueva):
   * confirmar abandono de la creación y volver al catálogo.
   */
  onNewRuleFirstAccessAbandon(): void {
    this._confirmationService.confirm({
      message: this._translate.instant('WXRL.CREATE.CONFIRM_ABANDON_FIRST_ACCESS'),
      header: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this._translate.instant('OK'),
      rejectLabel: this._translate.instant('CANCEL'),
      accept: () => {
        this.clearAutoSave();
        void this._router.navigate(['/wxrl/rules']);
      },
    });
  }

  onCancel(): void {
    if (this.isNewRule && this.hasUnsavedChanges()) {
      this._confirmationService.confirm({
        message: 'WXRL.CREATE.CONFIRM_CANCEL',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.clearAutoSave();
          this._router.navigate(['/wxrl/rules']);
        }
      });
    } else {
      if (this.isNewRule) {
        this.clearAutoSave();
      }
      this._router.navigate(['/wxrl/rules']);
    }
  }

  onCancelEdit(): void {
    if (this.ruleForm.dirty) {
      this._confirmationService.confirm({
        message: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_CANCEL_EDIT'),
        header: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this._translate.instant('OK'),
        rejectLabel: this._translate.instant('CANCEL'),
        accept: () => {
          this.loadRule(this.rule.ruleNr).then(() => {
            this.editMode = false;
            this.syncRuleHeaderControlsEditState();
            this.ruleForm.markAsPristine();
          });
        }
      });
    } else {
      this.editMode = false;
      this.syncRuleHeaderControlsEditState();
    }
  }
}
